<script setup lang="ts">
import { onMounted, ref } from "vue";

import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import TextInput from "@/components/ui/TextInput.vue";
import { useApi } from "@/services/api";
import { useAuthStore } from "@/stores/auth";
import type { OnboardingStepEmits } from "@/views/onboarding/types";

const emit = defineEmits<OnboardingStepEmits>();

const api = useApi();
const auth = useAuthStore();

const isWorkspaceNameAvailable = ref(true);
const workspaceName = ref<string>("");
const isLoading = ref(false);
const suggestedWorkspaceName = ref<string | null>(null);

async function getSuggestedWorkspaceName() {
  const user = auth.currentUser;

  const naiveSuggestedWorkspaceName =
    user?.first_name && user.first_name.length > 0 && user.first_name.toLowerCase() !== "none"
      ? `${user.first_name.replace(" ", "-")}-workspace`
      : (user?.email.split("@")[0] ?? "");

  const { available, suggested_slug } = await api.checkWorkspaceNameAvailability(
    naiveSuggestedWorkspaceName
  );
  isWorkspaceNameAvailable.value = available;
  workspaceName.value = suggested_slug ?? naiveSuggestedWorkspaceName;
}

async function createWorkspace() {
  isLoading.value = true;
  const userId = auth.currentUser?.id ?? "";
  if (userId.length > 0) {
    const workspaceId = await api.createWorkspace(workspaceName.value, userId);
    if (workspaceId) {
      emit("continue", { workspaceId });
    }
  }
  isLoading.value = false;
}

async function checkWorkspaceNameAvailability() {
  const { available, suggested_slug } = await api.checkWorkspaceNameAvailability(
    workspaceName.value
  );
  isWorkspaceNameAvailable.value = available;
  suggestedWorkspaceName.value = suggested_slug;
}

onMounted(async () => {
  await getSuggestedWorkspaceName();
});
</script>

<template>
  <div class="onboarding-create-workspace-step">
    <div class="form">
      <TextInput
        placeholder="Enter your workspace name"
        label="Workspace name"
        v-model="workspaceName"
        @keyup.enter="createWorkspace"
        @keyup="checkWorkspaceNameAvailability"
      />

      <Transition name="fade">
        <div v-if="!isWorkspaceNameAvailable && suggestedWorkspaceName" class="error">
          This workspace name is not unique, try something else. (like
          <a
            href="#"
            @click="
              workspaceName = suggestedWorkspaceName;
              isWorkspaceNameAvailable = true;
            "
          >
            {{ suggestedWorkspaceName }}
          </a>
          )
        </div>
      </Transition>
    </div>

    <Transition name="fade" mode="out-in">
      <div v-if="isLoading" class="loader">
        <SkoreSpinner />
      </div>

      <div v-else class="actions">
        <SimpleButton label="Continue" @click="createWorkspace" />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.onboarding-create-workspace-step {
  display: flex;
  flex-direction: column;
  gap: var(--numbers-32);

  .form {
    display: flex;
    flex-direction: column;
    gap: var(--numbers-4);

    .error {
      color: var(--content-negative);
    }

    a {
      color: inherit;
    }
  }

  .loader {
    display: flex;
    height: var(--numbers-40);
    align-items: center;
    justify-content: center;

    .skore-spinner {
      height: 100%;
    }
  }

  .actions {
    display: flex;
    width: 100%;

    .skore-button {
      flex: 1;
    }
  }
}
</style>
